/* eslint-disable jsx-a11y/anchor-is-valid */
import clsx from 'clsx'
import {
  useQueryResponseLoading,
  useQueryResponsePagination,
} from '../../core/QueryResponseProvider'
import {useQueryRequest} from '../../core/QueryRequestProvider'
import {PaginationState} from '../../../../../../../_metronic/helpers'
import {useMemo} from 'react'

const mappedLabel = (label: string): string => {
  if (label === '&laquo; Previous') return 'Previous'
  if (label === 'Next &raquo;') return 'Next'
  return label
}

const BlockUserListPagination = () => {
  const pagination = useQueryResponsePagination()
  const isLoading = useQueryResponseLoading()
  const {updateState} = useQueryRequest()

  const total = pagination?.total || 0
  const page = pagination?.page || 1
  const perPage = pagination?.items_per_page || 15

  // ✅ correct last page calculation
  const lastPage = Math.max(1, Math.ceil(total / perPage))

  const updatePage = (pageNumber: number | undefined | null) => {
    if (
      !pageNumber ||
      isLoading ||
      pageNumber === page ||
      pageNumber < 1 ||
      pageNumber > lastPage
    ) {
      return
    }

    const itemsPerPage = pagination.items_per_page as any
    updateState({page: pageNumber, items_per_page: itemsPerPage || 15})
  }

  const PAGINATION_PAGES_COUNT = 5

  const sliceLinks = (pagination?: PaginationState) => {
    if (!pagination?.links?.length) return []

    let scopedLinks = [...pagination.links]

    let previousLink = scopedLinks.shift()!
    let nextLink = scopedLinks.pop()!

    let pageLinks: typeof scopedLinks = []

    const halfOfPagesCount = Math.floor(PAGINATION_PAGES_COUNT / 2)

    pageLinks.push(previousLink)

    if (
      pagination.page <= Math.round(PAGINATION_PAGES_COUNT / 2) ||
      scopedLinks.length <= PAGINATION_PAGES_COUNT
    ) {
      pageLinks = [...pageLinks, ...scopedLinks.slice(0, PAGINATION_PAGES_COUNT)]
    }

    if (
      pagination.page > scopedLinks.length - halfOfPagesCount &&
      scopedLinks.length > PAGINATION_PAGES_COUNT
    ) {
      pageLinks = [
        ...pageLinks,
        ...scopedLinks.slice(scopedLinks.length - PAGINATION_PAGES_COUNT),
      ]
    }

    if (
      !(
        pagination.page <= Math.round(PAGINATION_PAGES_COUNT / 2) ||
        scopedLinks.length <= PAGINATION_PAGES_COUNT
      ) &&
      !(pagination.page > scopedLinks.length - halfOfPagesCount)
    ) {
      pageLinks = [
        ...pageLinks,
        ...scopedLinks.slice(
          pagination.page - 1 - halfOfPagesCount,
          pagination.page + halfOfPagesCount
        ),
      ]
    }

    pageLinks.push(nextLink)

    return pageLinks
  }

  const paginationLinks = useMemo(() => sliceLinks(pagination as any), [pagination])

  const from = total === 0 ? 0 : (page - 1) * perPage + 1
  const to = total === 0 ? 0 : Math.min(page * perPage, total)
  const entryLabel = total === 1 ? 'entry' : 'entries'

  return (
    <div className='row py-3'>
      <div className='col-sm-12 col-md-5 d-flex align-items-center justify-content-center justify-content-md-start'>
        <div className='dataTables_info'>
          {total === 0 ? (
            'No entries found'
          ) : (
            <>
              Showing {from} to {to} of {total} {entryLabel}
            </>
          )}
        </div>
      </div>

      <div className='col-sm-12 col-md-7 d-flex align-items-center justify-content-center justify-content-md-end'>
        <div>
          <ul className='pagination'>
            
            {/* ✅ First */}
            <li
              className={clsx('page-item', {
                disabled: isLoading || page === 1,
              })}
            >
              <a
                onClick={() => updatePage(1)}
                className='page-link'
                style={{cursor: page === 1 ? 'not-allowed' : 'pointer'}}
              >
                First
              </a>
            </li>

            {/* ✅ Previous + Numbers + Next */}
            {paginationLinks
              ?.map((link) => ({...link, label: mappedLabel(link.label)}))
              .map((link) => {
                const isPrevious = link.label === 'Previous'
                const isNext = link.label === 'Next'

                return (
                  <li
                    key={link.label}
                    className={clsx('page-item', {
                      active: page === link.page,
                      disabled:
                        isLoading ||
                        (isPrevious && page === 1) ||
                        (isNext && page === lastPage),
                      previous: isPrevious,
                      next: isNext,
                    })}
                  >
                    <a
                      className={clsx('page-link', {
                        'page-text': isPrevious || isNext,
                        'me-5': isPrevious,
                      })}
                      onClick={() => updatePage(link.page)}
                      style={{
                        cursor:
                          (isPrevious && page === 1) || (isNext && page === lastPage)
                            ? 'not-allowed'
                            : 'pointer',
                      }}
                    >
                      {link.label}
                    </a>
                  </li>
                )
              })}

            {/* ✅ Last */}
            <li
              className={clsx('page-item', {
                disabled: isLoading || page === lastPage,
              })}
            >
              <a
                onClick={() => updatePage(lastPage)}
                className='page-link'
                style={{cursor: page === lastPage ? 'not-allowed' : 'pointer'}}
              >
                Last
              </a>
            </li>

          </ul>
        </div>
      </div>
    </div>
  )
}

export {BlockUserListPagination}